import { CaretRight } from "@phosphor-icons/react";
// eslint-disable-next-line import/named
import { Link } from "@tanstack/react-router";
import cx from "classnames";

/**
 * Shared breadcrumb-header type scale (size + weight). The Ask Puntt chat and
 * project headers standardized on this, and the tickets header is aligned to it
 * so breadcrumb headers stay visually cohesive across the app: 14px / medium.
 */
export const BREADCRUMB_HEADER_TEXT_CLASS = "text-sm font-medium";

/**
 * Shared treatment for the FINAL (title) breadcrumb segment — the chat/project
 * title and the tickets ticket title. Centralized here so all three breadcrumb
 * headers render the title at exactly the same size / weight / color and cannot
 * drift apart: 14px / medium / neutral-gray-11.
 */
export const BREADCRUMB_TITLE_TEXT_CLASS =
  "text-sm font-medium text-puntt-neutral-gray-11";

interface BreadcrumbHeaderRootProps {
  /** Route/path the root segment links to (the section landing page). */
  to: string;
  /** Visible label, doubling as the link's accessible name. */
  label: string;
  /** Stable testid for the root link. */
  testId: string;
  /**
   * Render the trailing caret separator. Omit when there is no following
   * breadcrumb segment (e.g. a chat with no title yet) so no dangling caret
   * shows.
   */
  showSeparator?: boolean;
}

/**
 * The leading segment of a breadcrumb header — a link to the section root
 * followed by a decorative caret separator. Shared by the Ask Puntt chat and
 * project headers so both render an identical `Root › <title>` breadcrumb at
 * the same {@link BREADCRUMB_HEADER_TEXT_CLASS} type scale; only the root label
 * ("Ask Puntt" vs "All projects") and the trailing title segment differ.
 */
export function BreadcrumbHeaderRoot({
  to,
  label,
  testId,
  showSeparator = true,
}: BreadcrumbHeaderRootProps) {
  return (
    <>
      <Link to={to} className="puntt-link shrink-0" data-testid={testId}>
        <span
          className={cx(
            BREADCRUMB_HEADER_TEXT_CLASS,
            "text-puntt-accent-11 hover:underline",
          )}
        >
          {label}
        </span>
      </Link>
      {showSeparator && (
        <CaretRight
          size={12}
          aria-hidden="true"
          data-testid="breadcrumb-header-caret"
          className="shrink-0 text-puntt-neutral-gray-9"
        />
      )}
    </>
  );
}
